import {
  DEV_FEATURE_FLAG_OVERRIDE_TTL_SECONDS,
  isDevFeatureFlagOverrideEnvironment,
} from "@aix/vidmax-shared/src/analytics/featureFlags"
import type { IFeatureFlagOverride } from "@aix/vidmax-shared/src/analytics/featureFlags"
import posthog from "posthog-js"
import { useSyncExternalStore } from "react"

import { AIX_ENV } from "~/lib/env"

const FLAG_OVERRIDES_KEY = "vidmax-dev-flag-overrides"
const BROWSER_TTL_SAFETY_MARGIN_MS = 60_000
const FLAG_OVERRIDE_TTL_MS = DEV_FEATURE_FLAG_OVERRIDE_TTL_SECONDS * 1_000 - BROWSER_TTL_SAFETY_MARGIN_MS
const EMPTY_OVERRIDES: Readonly<Record<string, IFeatureFlagOverride>> = {}
const listeners = new Set<() => void>()
const OVERRIDES_AVAILABLE = isDevFeatureFlagOverrideEnvironment(AIX_ENV)

interface IStoredFeatureFlagOverrides {
  expiresAt: number
  overrides: Record<string, IFeatureFlagOverride>
  ownerId: string
}

const decodeOverrides = (rawOverrides: unknown): Record<string, IFeatureFlagOverride> => {
  if (!rawOverrides || typeof rawOverrides !== "object") return {}

  return Object.fromEntries(
    Object.entries(rawOverrides).flatMap(([key, rawOverride]) => {
      if (!rawOverride || typeof rawOverride !== "object" || !("value" in rawOverride)) return []
      const value = (rawOverride as { value?: unknown }).value
      if (typeof value !== "boolean" && typeof value !== "string") return []
      return [[key, rawOverride as IFeatureFlagOverride]]
    }),
  )
}

export const parseStoredFeatureFlagOverrides = (
  stored: string,
  now: number,
  ownerId: string,
): Record<string, IFeatureFlagOverride> | null => {
  try {
    const parsed = JSON.parse(stored) as Partial<IStoredFeatureFlagOverrides> | null
    if (!parsed || parsed.ownerId !== ownerId || typeof parsed.expiresAt !== "number" || parsed.expiresAt <= now) {
      return null
    }
    return decodeOverrides(parsed.overrides)
  } catch {
    return null
  }
}

const readStoredOverrides = (ownerId: string): Record<string, IFeatureFlagOverride> => {
  if (typeof window === "undefined") return {}
  const stored = window.localStorage.getItem(FLAG_OVERRIDES_KEY)
  if (!stored) return {}
  if (!OVERRIDES_AVAILABLE) return {}

  return parseStoredFeatureFlagOverrides(stored, Date.now(), ownerId) ?? {}
}

let currentOwnerId: string | undefined
let currentOverrides: Record<string, IFeatureFlagOverride> = {}

const applyPostHogValueOverrides = (overrides: Record<string, IFeatureFlagOverride>): void => {
  const valueOverrides = Object.fromEntries(Object.entries(overrides).map(([key, override]) => [key, override.value]))
  if (Object.keys(valueOverrides).length === 0) {
    posthog.featureFlags?.overrideFeatureFlags(false)
    return
  }
  posthog.featureFlags?.overrideFeatureFlags(valueOverrides)
}

export const getFeatureFlagOverrides = (): Record<string, IFeatureFlagOverride> => currentOverrides

export const hydrateFeatureFlagOverridesForOwner = (ownerId: string): void => {
  currentOwnerId = ownerId
  currentOverrides = OVERRIDES_AVAILABLE ? readStoredOverrides(ownerId) : {}
  applyPostHogValueOverrides(currentOverrides)
  listeners.forEach((listener) => listener())
}

export const clearFeatureFlagOverrideOwner = (): void => {
  currentOwnerId = undefined
  currentOverrides = {}
  applyPostHogValueOverrides(currentOverrides)
  listeners.forEach((listener) => listener())
}

export const setFeatureFlagOverrides = (overrides: Record<string, IFeatureFlagOverride>): void => {
  const ownerId = currentOwnerId
  currentOverrides = OVERRIDES_AVAILABLE && ownerId ? overrides : {}
  if (typeof window !== "undefined") {
    if (Object.keys(currentOverrides).length === 0 || !ownerId) {
      window.localStorage.removeItem(FLAG_OVERRIDES_KEY)
    } else {
      const stored: IStoredFeatureFlagOverrides = {
        expiresAt: Date.now() + FLAG_OVERRIDE_TTL_MS,
        overrides: currentOverrides,
        ownerId,
      }
      window.localStorage.setItem(FLAG_OVERRIDES_KEY, JSON.stringify(stored))
    }
  }
  applyPostHogValueOverrides(currentOverrides)
  listeners.forEach((listener) => listener())
}

export const applyStoredFeatureFlagOverrides = (): void => {
  applyPostHogValueOverrides(currentOverrides)
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const useDevFeatureFlagOverride = (flagKey: string): IFeatureFlagOverride | undefined => {
  const overrides = useSyncExternalStore(subscribe, getFeatureFlagOverrides, () => EMPTY_OVERRIDES)
  return overrides[flagKey]
}

export const useFeatureFlagOverrideCount = (): number => {
  const overrides = useSyncExternalStore(subscribe, getFeatureFlagOverrides, () => EMPTY_OVERRIDES)
  return Object.keys(overrides).length
}
